import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Button } from '@heroui/react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
        <div className="container mx-auto px-6 py-6 max-w-4xl">
          <Button
            as={RouterLink}
            to="/"
            variant="light"
            startContent={<ArrowLeftIcon className="w-4 h-4" />}
            className="mb-4"
          >
            Back to Home
          </Button>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-3">Terms of Service</h1>
          <div className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
            <p><strong>Provider:</strong> Rimal Labs LLC</p>
            <p><strong>Site/App:</strong> ClipKite (ClipKite.com)</p>
            <p><strong>Contact:</strong> <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link></p>
            <p><strong>Governing law & venue:</strong> Arizona, USA</p>
            <p><strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="prose prose-gray max-w-none">

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">1. Acceptance</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              By using ClipKite.com (the "Service"), you agree to these Terms.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">2. Eligibility & Accounts</h2>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300">
              <li><strong className="text-gray-900 dark:text-slate-100">Age:</strong> 16+ only.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Auth:</strong> Firebase Authentication. Keep credentials secure.</li>
              <li>We may require email verification before posting/commenting.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">3. Your Content & License</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              You own your uploads. You grant Rimal Labs LLC a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to host, store, transcode, thumbnail, reproduce, adapt, publish, publicly perform, display, and distribute your content to operate, improve, and promote the Service. The license ends when you remove the content, except for prior uses, reasonable backups, or content others already re-shared.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">4. Content Rules</h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-50 mb-3 mt-6">Prohibited (removal; may be permanent ban):</h3>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300 mb-6">
              <li>Graphic violence/gore (severe injury, exposed organs, executions, dismemberment), animal cruelty, "snuff," or content that glorifies/incites harm.</li>
              <li>Nudity/sexual content (including sexualized minors; reported to authorities).</li>
              <li>Doxxing, hate speech, harassment, threats; illegal content or unlawful recording; spam/botting/metric manipulation.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-50 mb-3">Allowed with restrictions:</h3>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed mb-3">
              Non-graphic violence/blood (e.g., fights, minor injury) only if you:
            </p>
            <ol className="list-decimal list-inside space-y-3 text-gray-700 dark:text-slate-300 ml-4 mb-4">
              <li>Label as Violence/Blood;</li>
              <li>Blur/redact faces, plates, sensitive PII;</li>
              <li>Avoid glorifying harm; use a non-graphic thumbnail.</li>
            </ol>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Mislabeling or evasion can result in removal, age/warning screens, strikes, or bans.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">5. Safety & Evidence Disclaimer</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              We don't verify timestamps, chain-of-custody, or accuracy. Not legal advice.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">6. Moderation & Enforcement</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              We may remove content; restrict features; shadow-ban; suspend or permanently ban accounts—especially for nudity/sexual content or graphic violence. Report issues via email at <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link>. One appeal within 7 days; our decision is final.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">7. Third-Party Services and Modules</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              The Service may integrate third-party content, delivery, measurement, or safety services. Those providers' terms/policies apply to their components. We're not responsible for third-party content or availability.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">8. Intellectual Property / DMCA</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Send notices to <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link> with your contact info, the work, exact URLs, a statement under penalty of perjury, and your signature. A designated DMCA agent and mailing address will be posted once appointed; email notices are accepted meanwhile.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">9. Fees</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Currently free. If we add paid features, we'll post updated terms/pricing.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-primary-500">10. Termination</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              You may stop using the Service or request deletion any time. We may suspend or terminate for violations or risk.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">11. Disclaimers & Liability</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Service is provided "AS IS." To the fullest extent allowed, we and our suppliers aren't liable for indirect, incidental, special, consequential, or punitive damages, or loss of data, profits, or goodwill. Total liability is capped at US $100 or what you paid us in the last 12 months, whichever is greater.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">12. Indemnity</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              You agree to indemnify us for claims arising from your content or your violation of these Terms or law.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">13. Governing Law & Venue</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Arizona law (conflict rules excluded). Exclusive venue: state/federal courts in Arizona.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">14. Changes</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              We may update these Terms and will post the new date; material changes get reasonable notice. Continued use = acceptance.
            </p>
          </section>

          {/* Section 15 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">15. Contact</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Rimal Labs LLC — <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
